import { useEffect, useRef, useState } from 'react';

// Finger travel is halved into the indicator's travel, roughly matching the page's rubber band.
const DAMPING = 0.5;
export const PULL_THRESHOLD = 60;
const MAX_PULL = 90;

// Places a pull must not start from: overlays with their own scroll, drag handles, sliders.
const EXCLUDED = '.Modal, .BottomSheet, .HeaderSheet, .drag-handle, input[type="range"], [data-no-pull]';

// A home-screen launch has no browser chrome, and with it no native pull-to-refresh.
const isStandalone = () =>
  window.navigator.standalone === true || window.matchMedia?.('(display-mode: standalone)').matches;

const pageIsLocked = () =>
  document.body.style.overflow === 'hidden' || document.body.classList.contains('GlobalLoaderOpen');

// True when the touch sits inside an inner scroller that isn't at its own top.
const insideScrolledContainer = (target) => {
  for (let el = target; el && el !== document.body; el = el.parentElement) {
    if (el.scrollTop > 0) return true;
  }
  return false;
};

/** Pull down from the top of the page to reload it. Returns the indicator's state. */
const usePullToRefresh = () => {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const gesture = useRef(null);

  useEffect(() => {
    if (!isStandalone()) return undefined;

    const onTouchStart = (e) => {
      gesture.current = null;
      if (e.touches.length !== 1 || window.scrollY > 0 || pageIsLocked()) return;
      if (e.target.closest?.(EXCLUDED) || insideScrolledContainer(e.target)) return;
      const { clientX, clientY } = e.touches[0];
      gesture.current = { x: clientX, y: clientY, pulling: false, distance: 0 };
    };

    const onTouchMove = (e) => {
      const g = gesture.current;
      if (!g) return;
      const dx = e.touches[0].clientX - g.x;
      const dy = e.touches[0].clientY - g.y;
      // Decide once the finger has clearly moved: a sideways or upward start is not a pull.
      if (!g.pulling) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        if (dy <= 0 || Math.abs(dx) > dy) {
          gesture.current = null;
          return;
        }
        g.pulling = true;
      }
      g.distance = Math.min(Math.max(dy, 0) * DAMPING, MAX_PULL);
      setPull(g.distance);
    };

    const onTouchEnd = () => {
      const g = gesture.current;
      gesture.current = null;
      if (g?.pulling && g.distance >= PULL_THRESHOLD) {
        setRefreshing(true);
        // One frame for the dots to show before the page goes.
        setTimeout(() => window.location.reload(), 150);
        return;
      }
      setPull(0);
    };

    const options = { passive: true };
    window.addEventListener('touchstart', onTouchStart, options);
    window.addEventListener('touchmove', onTouchMove, options);
    window.addEventListener('touchend', onTouchEnd, options);
    window.addEventListener('touchcancel', onTouchEnd, options);
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
    };
  }, []);

  return { pull, refreshing };
};

export default usePullToRefresh;
